import React, { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";

import { isIOSWebKit } from "@vahak/core-ui/dist/methods/browser";
import VahakCircleBlueLogo from "@vahak/core/dist/icons/VahakCircleBlueLogo.svg";

import DownloadAppAction from "../../common/download-app-qr-modal/DownloadAppAction";
import { COMPACT_APP_DOWNLOAD } from "../../homepage-v2/compactAppDownload.constants";
import Styles from "./SmartAppBanner.module.scss";

const STORAGE_KEY = "hpv2-smart-app-banner-dismissed";
const BANNER_HEIGHT = "68px";

type SmartAppBannerProps = {
    onDownload?: (store: "app_store" | "play_store") => void;
};

const SmartAppBanner = ({ onDownload }: SmartAppBannerProps) => {
    const { t } = useTranslation("navigation");
    const [isVisible, setIsVisible] = useState(false);
    const isApple = typeof navigator !== "undefined" && isIOSWebKit();
    const storeUrl = isApple ? COMPACT_APP_DOWNLOAD.appStoreUrl : COMPACT_APP_DOWNLOAD.playStoreUrl;

    useEffect(() => {
        try {
            if (window.sessionStorage.getItem(STORAGE_KEY) === "1") {
                return;
            }
        } catch {
            // Private mode — still show.
        }

        if (!window.matchMedia("(max-width: 991px)").matches) {
            return;
        }

        setIsVisible(true);
    }, []);

    useEffect(() => {
        const height = isVisible ? BANNER_HEIGHT : "0px";
        document.documentElement.style.setProperty("--hp-v2-smart-banner-height", height);
        return () => {
            document.documentElement.style.setProperty("--hp-v2-smart-banner-height", "0px");
        };
    }, [isVisible]);

    const handleDismiss = () => {
        try {
            window.sessionStorage.setItem(STORAGE_KEY, "1");
        } catch {
            // Private mode.
        }
        setIsVisible(false);
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className={Styles.banner} role="region" aria-label={t("appDownload.smartBanner.title")}>
            <button type="button" className={Styles.dismiss} onClick={handleDismiss} aria-label={t("appDownload.smartBanner.dismiss")}>
                ×
            </button>
            <span className={Styles.icon} aria-hidden>
                <VahakCircleBlueLogo />
            </span>
            <div className={Styles.copy}>
                <p className={Styles.title}>{t("appDownload.smartBanner.title")}</p>
                <p className={Styles.tagline}>{t("appDownload.smartBanner.tagline")}</p>
            </div>
            <DownloadAppAction
                href={storeUrl}
                storeUrl={storeUrl}
                className={Styles.cta}
                onClick={() => onDownload?.(isApple ? "app_store" : "play_store")}
                aria-label={isApple ? t("appDownload.downloadAppStore") : t("appDownload.downloadPlayStore")}
            >
                {t("appDownload.smartBanner.cta")}
            </DownloadAppAction>
        </div>
    );
};

export default SmartAppBanner;
